import React, { useState } from 'react';
import {
  Controller,
  FieldError,
  FieldValues,
  Path,
  PathValue,
  UnpackNestedValue,
  UseFormReturn,
} from 'react-hook-form';
import get from 'lodash/get';
import { Button } from '../../../../CommonComponents/OPL/Button/Button';
import { InputNumberMask } from '../../../../CommonComponents/OPL/Input/InputNumberMask';
import { Select } from '../../../../CommonComponents/OPL/Select/Select/Select';
import { IncomeOptionsDTO } from '../../api/useIncomeQuery';
import { AssetBucket } from '../components/AssetBucket/AssetBucket';
import { ManagementStyle } from '../components/ManagementStyle/ManagementStyle';
import { RealEstateFormValues } from './RealEstateForm.meta';

interface RealEstateFormFieldsProps<TFieldValues extends FieldValues = RealEstateFormValues> {
  formBag: UseFormReturn<TFieldValues>;
  fieldNamePrefix?: string;
  options: IncomeOptionsDTO;
  defaultShowAdvanced?: boolean;
}

export const RealEstateFormFields = <TFieldValues extends FieldValues = RealEstateFormValues>({
  formBag: { control, setValue, watch, formState },
  fieldNamePrefix = '',
  options,
  defaultShowAdvanced = false,
}: RealEstateFormFieldsProps<TFieldValues>) => {
  const [showAdvanced, setShowAdvanced] = useState(defaultShowAdvanced);

  const { RealEstateAssetOptions } = options;

  const fieldName = <K extends keyof RealEstateFormValues>(name: K) =>
    `${fieldNamePrefix}${String(name)}` as Path<TFieldValues>;

  const managementStyleValue = watch(fieldName('OrionManagementStyleId')) as number | null;

  return (
    <>
      <Controller
        name={fieldName('AssociatedAssetId')}
        control={control}
        render={({ field: { value, onChange } }) => (
          <Select
            label="Real Estate Asset"
            value={RealEstateAssetOptions.find(({ value: v }) => v === value)}
            options={RealEstateAssetOptions}
            onChange={option => onChange(option ? option.value : null)}
            formVariant={
              get(formState.errors, fieldName('AssociatedAssetId')) ? 'error' : undefined
            }
            helperText={
              (get(formState.errors, fieldName('AssociatedAssetId')) as FieldError | undefined)
                ?.message
            }
            isPortal
          />
        )}
      />

      <Controller
        control={control}
        name={fieldName('Amount')}
        render={({ field: { onChange, ...field } }) => (
          <InputNumberMask
            {...field}
            label="Pre-Tax Income"
            decimalScale={2}
            allowNegative={false}
            leftIcon="far fa-dollar-sign"
            innerText="/year"
            onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
            formVariant={get(formState.errors, fieldName('Amount')) ? 'error' : undefined}
            helperText={
              (get(formState.errors, fieldName('Amount')) as FieldError | undefined)?.message
            }
            tooltip="This is your pre-tax net income from the property."
          />
        )}
      />

      <ManagementStyle
        control={control}
        options={options}
        fieldName={fieldName('OrionManagementStyleId')}
        onChange={(value: number | null) => {
          setValue(
            fieldName('CustomBucketIndex'),
            value as UnpackNestedValue<PathValue<TFieldValues, Path<TFieldValues>>>,
          );
        }}
      />

      <AssetBucket
        control={control}
        fieldName={fieldName('CustomBucketIndex')}
        managementStyleValue={managementStyleValue}
        options={options}
      />

      <Button
        variant="link"
        leftIcon={showAdvanced ? 'fas fa-chevron-down' : 'fas fa-chevron-right'}
        onClick={() => setShowAdvanced(!showAdvanced)}
        aria-expanded={showAdvanced}
        size="small"
        mr="auto"
      >
        {showAdvanced ? 'Hide' : 'Show'} Advanced
      </Button>

      {showAdvanced && (
        <>
          <Controller
            control={control}
            name={fieldName('IncomeGrowth')}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Income Growth"
                rightIcon="far fa-percent"
                decimalScale={2}
                allowNegative={false}
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip='Enter the income growth rate applied to the Pre-Tax Income field. Leave this field blank to use the global "Income Growth Rate" found in Settings.'
              />
            )}
          />

          <Controller
            control={control}
            name={fieldName('YearlyTaxableAmount')}
            render={({ field: { onChange, ...field } }) => (
              <InputNumberMask
                {...field}
                label="Taxable Income"
                decimalScale={2}
                allowNegative={false}
                leftIcon="far fa-dollar-sign"
                innerText="/year"
                onValueChange={({ floatValue }) => onChange(floatValue ?? null)}
                tooltip="Enter a taxable amount if the amount to be taxed is different than the pre-tax income."
              />
            )}
          />
        </>
      )}
    </>
  );
};
